import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { MobileAppService } from 'app/core/services/mobile-app.service';
import { MobilePlatform } from 'app/utils/mobile-platform.utils';

/**
 * Public pages opened from outside the entry flow (the mobile app links to
 * the legal pages, password reset emails link to `/reset`), where the prompt
 * would only get in the way.
 */
const PROMPT_EXCLUDED_PATHS = [
  '/politica-privacidade',
  '/termos-uso',
  '/reset',
];
const QUERY_STRING_SEPARATOR = '?';

const DOWNLOAD_LABEL_KEYS: Record<MobilePlatform, string> = {
  android: 'mobile-app-prompt.download-google-play',
  ios: 'mobile-app-prompt.download-app-store',
};

/**
 * Asks phone and tablet visitors whether they want the IsoFarm mobile app or
 * prefer to keep using the web app. It never redirects on its own, because a
 * visit from the printed QR code cannot be told apart from a typed URL.
 */
@Component({
  standalone: true,
  selector: 'app-mobile-app-prompt',
  templateUrl: './mobile-app-prompt.component.html',
  styleUrls: ['./mobile-app-prompt.component.scss'],
  imports: [TranslateModule],
})
export class MobileAppPromptComponent implements OnInit, OnDestroy {
  isVisible = false;
  downloadLabelKey = '';

  private platform: MobilePlatform | null = null;
  private readonly mobileAppService = inject(MobileAppService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.platform = this.mobileAppService.getPlatform();
    if (!this.platform || this.mobileAppService.isPromptDismissed()) {
      return;
    }
    this.downloadLabelKey = DOWNLOAD_LABEL_KEYS[this.platform];

    // Wait for the first completed navigation to an entry page, so the prompt
    // never flashes while the `/app` route is redirecting to the store.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        filter((event) => isPromptAllowed(event.urlAfterRedirects)),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isVisible = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  downloadApp(): void {
    this.mobileAppService.dismissPrompt();
    this.isVisible = false;
    this.mobileAppService.openStore(this.platform);
  }

  continueOnWeb(): void {
    this.mobileAppService.dismissPrompt();
    this.isVisible = false;
  }
}

function isPromptAllowed(url: string): boolean {
  const [path] = url.split(QUERY_STRING_SEPARATOR);
  return !PROMPT_EXCLUDED_PATHS.includes(path);
}
